import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { PopupStyle } from "../types";

export interface PopupStyleConfig {
  label: string;
  emoji: string;
  headerBg: string;
  headerColor: string;
  buttonColor: string;
  animation: "confetti" | "pulse" | "none";
}

export const POPUP_STYLE_LIST: PopupStyle[] = [
  "congrats",
  "important",
  "notice",
  "update",
  "neutral_teal",
  "neutral_indigo",
  "neutral_rose",
  "neutral_slate",
];

export const POPUP_STYLE_CONFIG: Record<PopupStyle, PopupStyleConfig> = {
  congrats: {
    label: "Congrats",
    emoji: "🎉",
    headerBg: "linear-gradient(135deg, #43a047 0%, #fbc02d 100%)",
    headerColor: "#fff",
    buttonColor: "#2e7d32",
    animation: "confetti",
  },
  important: {
    label: "Important Info",
    emoji: "⚠️",
    headerBg: "linear-gradient(135deg, #d32f2f 0%, #f57c00 100%)",
    headerColor: "#fff",
    buttonColor: "#d32f2f",
    animation: "pulse",
  },
  notice: {
    label: "Notice",
    emoji: "📢",
    headerBg: "#1976d2",
    headerColor: "#fff",
    buttonColor: "#1976d2",
    animation: "none",
  },
  update: {
    label: "Update / Reminder",
    emoji: "🔧",
    headerBg: "#6a1b9a",
    headerColor: "#fff",
    buttonColor: "#6a1b9a",
    animation: "none",
  },
  neutral_teal: {
    label: "Teal",
    emoji: "🔵",
    headerBg: "#00897b",
    headerColor: "#fff",
    buttonColor: "#00897b",
    animation: "none",
  },
  neutral_indigo: {
    label: "Indigo",
    emoji: "🔷",
    headerBg: "#3949ab",
    headerColor: "#fff",
    buttonColor: "#3949ab",
    animation: "none",
  },
  neutral_rose: {
    label: "Rose",
    emoji: "🌸",
    headerBg: "#c2185b",
    headerColor: "#fff",
    buttonColor: "#c2185b",
    animation: "none",
  },
  neutral_slate: {
    label: "Slate",
    emoji: "⬛",
    headerBg: "#37474f",
    headerColor: "#fff",
    buttonColor: "#37474f",
    animation: "none",
  },
};

// Confetti overlay used by the "congrats" style — shared by the admin live preview
// and the real player-facing dialog so they always look identical.
export function PopupConfetti() {
  const confettiPieces = useMemo(
    () =>
      [...Array(30)].map(() => ({
        left: `${Math.random() * 100}%`,
        color: ["#FFD700", "#FF6B6B", "#4ECDC4", "#95E1D3", "#F38181", "#A78BFA", "#34D399"][
          Math.floor(Math.random() * 7)
        ],
        delay: `${Math.random() * 2}s`,
        duration: `${2 + Math.random() * 3}s`,
        rotation: 360 + Math.random() * 360,
        shape: Math.random() > 0.5 ? "circle" : "rect",
      })),
    []
  );

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: "none",
        zIndex: 1,
        overflow: "hidden",
      }}
    >
      {confettiPieces.map((piece, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            top: "-12px",
            left: piece.left,
            width: piece.shape === "circle" ? "8px" : "6px",
            height: piece.shape === "circle" ? "8px" : "12px",
            borderRadius: piece.shape === "circle" ? "50%" : "2px",
            backgroundColor: piece.color,
            animation: `popupConfettiFall ${piece.duration} linear infinite`,
            animationDelay: piece.delay,
            "@keyframes popupConfettiFall": {
              "0%": { transform: "translateY(0) rotate(0deg)", opacity: 1 },
              "100%": {
                transform: `translateY(120vh) rotate(${piece.rotation}deg)`,
                opacity: 0,
              },
            },
          }}
        />
      ))}
    </Box>
  );
}

export interface PopupHeaderProps {
  style: PopupStyle;
  title: string;
  imageUrl?: string;
  badge?: React.ReactNode;
  /** "hero" is used by the real pop-up dialogs (taller image); "compact" by admin previews. */
  size?: "compact" | "hero";
}

// Renders the pop-up's title area — a flat/gradient color block with the
// emoji/title text. When an image is attached, the photo is rendered above
// this block (never behind/under the text) so the title never covers the
// picture, which matters most on narrow phone screens. Shared by the admin
// live previews, the player dashboard dialog, and the public homepage
// dialog so all three always look identical for a given style/image
// combination.
export function PopupHeader({ style, title, imageUrl, badge, size = "compact" }: PopupHeaderProps) {
  const cfg = POPUP_STYLE_CONFIG[style];

  const textBlock = (
    <Box
      sx={{
        position: "relative",
        background: cfg.headerBg,
        color: cfg.headerColor,
        px: size === "hero" ? 3 : 2,
        py: 2,
        ...(cfg.animation === "pulse" && {
          animation: "popupHeaderPulse 1.6s ease-in-out infinite",
          "@keyframes popupHeaderPulse": {
            "0%": { boxShadow: "0 0 0 0 rgba(211,47,47,0.5)" },
            "70%": { boxShadow: "0 0 0 10px rgba(211,47,47,0)" },
            "100%": { boxShadow: "0 0 0 0 rgba(211,47,47,0)" },
          },
        }),
      }}
    >
      {!imageUrl && cfg.animation === "confetti" && <PopupConfetti />}
      <Typography
        variant={size === "hero" ? "h5" : "h6"}
        fontWeight={800}
        sx={{ position: "relative", zIndex: 2 }}
      >
        {cfg.emoji} {title}
        {badge}
      </Typography>
    </Box>
  );

  if (imageUrl) {
    const imageHeight = size === "hero" ? 220 : 140;
    return (
      <Box>
        <Box sx={{ position: "relative", height: imageHeight, overflow: "hidden" }}>
          <Box
            component="img"
            src={imageUrl}
            alt=""
            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
          {cfg.animation === "confetti" && <PopupConfetti />}
        </Box>
        {textBlock}
      </Box>
    );
  }

  return textBlock;
}

// Internal-path vs external-link navigation for a pop-up's CTA button.
// Shared by the player dashboard dialog and the public homepage dialog.
export function openPopupLink(url?: string) {
  if (!url) return;
  if (url.startsWith("/")) {
    window.location.href = url;
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}
